"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import type { Matcher } from "react-day-picker";
import { cn } from "../utils";
import { RequiredMark } from "../required-mark";
import { Calendar } from "../calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
export interface InputDateProps {
  name: string;
  id?: string;
  label?: string | null;
  /** Helper text rendered below the field — plain string or rich content (e.g. a description with a link) */
  description?: React.ReactNode;
  placeholder?: string;
  value?: string;
  onChange?: (iso: string) => void;
  onBlur?: () => void;
  required?: boolean;
  error?: string;
  minDate?: string | null;
  maxDate?: string | null;
  disabled?: boolean;
  className?: string;
}
function toDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
}
function toISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function formatDisplay(iso: string): string {
  return toDate(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}
export const InputDate = React.forwardRef<HTMLButtonElement, InputDateProps>((t0, ref) => {
  const $ = _c(77);
  const {
    name,
    id,
    label,
    description,
    placeholder: t1,
    value,
    onChange,
    onBlur,
    required,
    error,
    minDate,
    maxDate,
    disabled,
    className
  } = t0;
  const placeholder = t1 === undefined ? "Select a date" : t1;
  const generatedId = React.useId();
  const triggerId = id || name || generatedId;
  const descriptionId = `${triggerId}-description`;
  const [open, setOpen] = React.useState(false);
  const hasError = Boolean(error);
  const hasDescription = Boolean(error || description);
  let t2;
  if ($[0] !== value) {
    t2 = value ? toDate(value) : undefined;
    $[0] = value;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const selected = t2;
  let t3;
  if ($[2] !== minDate || $[3] !== selected) {
    t3 = selected ?? (minDate ? toDate(minDate) : undefined);
    $[2] = minDate;
    $[3] = selected;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const defaultMonth = t3;
  let matchers;
  if ($[5] !== maxDate || $[6] !== minDate) {
    matchers = [];
    if (minDate) {
      let t4;
      if ($[8] !== minDate) {
        t4 = toDate(minDate);
        $[8] = minDate;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      let t5;
      if ($[10] !== t4) {
        t5 = {
          before: t4
        };
        $[10] = t4;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      matchers.push(t5);
    }
    if (maxDate) {
      let t4;
      if ($[12] !== maxDate) {
        t4 = toDate(maxDate);
        $[12] = maxDate;
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      let t5;
      if ($[14] !== t4) {
        t5 = {
          after: t4
        };
        $[14] = t4;
        $[15] = t5;
      } else {
        t5 = $[15];
      }
      matchers.push(t5);
    }
    $[5] = maxDate;
    $[6] = minDate;
    $[7] = matchers;
  } else {
    matchers = $[7];
  }
  const disabledMatcher = matchers.length ? matchers : undefined;
  let t4;
  if ($[16] !== onChange) {
    t4 = date => {
      if (!date) {
        return;
      }
      onChange?.(toISO(date));
      setOpen(false);
    };
    $[16] = onChange;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleSelect = t4;
  let t5;
  if ($[18] !== onBlur) {
    t5 = next => {
      setOpen(next);
      if (!next) {
        onBlur?.();
      }
    };
    $[18] = onBlur;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleOpenChange = t5;
  let t6;
  if ($[20] !== className) {
    t6 = cn("group/input-date flex w-full flex-col gap-2", className);
    $[20] = className;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== disabled || $[23] !== label || $[24] !== required || $[25] !== triggerId) {
    t7 = Boolean(label) && <label htmlFor={triggerId} className={cn("typography-component-sm-medium desktop:typography-component-md-medium", disabled ? "text-input-text-disabled" : "text-input-text-label")}>{label}<RequiredMark required={required} /></label>;
    $[22] = disabled;
    $[23] = label;
    $[24] = required;
    $[25] = triggerId;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const t8 = !value || undefined;
  const t9 = hasDescription ? descriptionId : undefined;
  const t10 = hasError || undefined;
  const t11 = hasError && "bg-input-surface-error hover:bg-input-surface-error focus:bg-input-surface-error";
  const t12 = disabled && "pointer-events-none bg-input-surface-disabled text-input-text-disabled";
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = cn("flex h-12 w-full items-center justify-between rounded-sm border border-transparent px-4 py-3", "typography-body-sm-regular text-left", "bg-input-surface-rest", "outline-none transition-colors", "hover:bg-input-surface-hover", "focus:border-border-subtle focus:bg-input-surface-hover", "data-[state=open]:border-border-subtle data-[state=open]:bg-input-surface-hover", "data-[empty=true]:text-input-text-disabled", "text-input-text-rest", t11, t12);
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== placeholder || $[31] !== value) {
    t14 = value ? formatDisplay(value) : placeholder;
    $[30] = placeholder;
    $[31] = value;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t14) {
    t15 = <span className="truncate">{t14}</span>;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const t16 = hasError ? "text-input-text-error" : "text-text-secondary";
  let t17;
  if ($[35] !== t16) {
    t17 = cn("ml-3 size-5 shrink-0", t16);
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t17) {
    t18 = <CalendarIcon aria-hidden={true} className={t17} />;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== disabled || $[40] !== ref || $[41] !== t10 || $[42] !== t13 || $[43] !== t15 || $[44] !== t18 || $[45] !== t8 || $[46] !== t9 || $[47] !== triggerId) {
    t19 = <PopoverTrigger asChild={true}><button ref={ref} id={triggerId} type="button" disabled={disabled} data-empty={t8} aria-describedby={t9} aria-invalid={t10} className={t13}>{t15}{t18}</button></PopoverTrigger>;
    $[39] = disabled;
    $[40] = ref;
    $[41] = t10;
    $[42] = t13;
    $[43] = t15;
    $[44] = t18;
    $[45] = t8;
    $[46] = t9;
    $[47] = triggerId;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const t20 = label ? `${label} calendar` : "Date picker calendar";
  let t21;
  if ($[49] !== defaultMonth || $[50] !== disabledMatcher || $[51] !== handleSelect || $[52] !== selected) {
    t21 = <Calendar mode="single" selected={selected} onSelect={handleSelect} defaultMonth={defaultMonth} disabled={disabledMatcher} autoFocus={true} />;
    $[49] = defaultMonth;
    $[50] = disabledMatcher;
    $[51] = handleSelect;
    $[52] = selected;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== t20 || $[55] !== t21) {
    t22 = <PopoverContent align="start" className="w-auto p-0" aria-label={t20}>{t21}</PopoverContent>;
    $[54] = t20;
    $[55] = t21;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== handleOpenChange || $[58] !== open || $[59] !== t19 || $[60] !== t22) {
    t23 = <Popover open={open} onOpenChange={handleOpenChange}>{t19}{t22}</Popover>;
    $[57] = handleOpenChange;
    $[58] = open;
    $[59] = t19;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  const t24 = value ?? "";
  let t25;
  if ($[62] !== name || $[63] !== t24) {
    t25 = <input type="hidden" name={name} value={t24} />;
    $[62] = name;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== description || $[66] !== descriptionId || $[67] !== error || $[68] !== hasDescription || $[69] !== hasError) {
    t26 = hasDescription && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>{error || description}</p>;
    $[65] = description;
    $[66] = descriptionId;
    $[67] = error;
    $[68] = hasDescription;
    $[69] = hasError;
    $[70] = t26;
  } else {
    t26 = $[70];
  }
  let t27;
  if ($[71] !== t23 || $[72] !== t25 || $[73] !== t26 || $[74] !== t6 || $[75] !== t7) {
    t27 = <div data-testid="input-date" className={t6}>{t7}{t23}{t25}{t26}</div>;
    $[71] = t23;
    $[72] = t25;
    $[73] = t26;
    $[74] = t6;
    $[75] = t7;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  return t27;
});
InputDate.displayName = "InputDate";